import React from "react";
import { gql } from "graphql-tag";
import { useMutation } from "@apollo/client";
import { Form, Formik } from "formik";
import { Alert, Button, DialogActions, DialogContent } from "@mui/material";
import * as y from "yup";
import { Modal } from "components/haven-design";
import { Form as HavenForm, SubmitButton } from "components/haven-design/form";
import { Checkbox } from "components/haven-design/form/checkbox";
import { SelectOrganization } from "components/haven-design/form/special-fields";
import { useSnackbar } from "components/haven-design/snackbar";
import { getAllProperties_properties as PropertyData, TransferProperty, TransferPropertyVariables } from "types/gql-types";
import logger from "services/logger";

const TRANSFER_PROPERTY = gql`
  mutation TransferProperty($propertyId: ID!, $organizationId: ID!) {
    transferProperty(propertyId: $propertyId, organizationId: $organizationId)
  }
`;

type TransferPropertyModalProps = {
  property: PropertyData | null;
  open: boolean;
  onClose: () => void;
};

const buildFormSchema = (currentOrganizationId?: string | null) => y.object().shape({
  organizationId: y
    .string()
    .trim()
    .required("Organization is required")
    .notOneOf(
      [currentOrganizationId || ""],
      "Choose a different organization",
    ),
  confirmed: y.boolean().oneOf([true], "Must be checked to continue"),
});

const useTransferProperty = (
  property: PropertyData | null,
  onClose: () => void,
) => {
  const { openSnackbar } = useSnackbar();

  const [transferProperty, { loading }] = useMutation<
  TransferProperty,
  TransferPropertyVariables
  >(TRANSFER_PROPERTY);

  const handleSubmit = async ({ organizationId }: { organizationId: string }) => {
    if (!property) return;

    try {
      await transferProperty({
        variables: {
          propertyId: property.id,
          organizationId,
        },
      });

      openSnackbar({
        message:
          `Transfer queued for ${property.name}. The property will update when the job completes.`,
        status: "success",
      });
      onClose();
    } catch (error) {
      openSnackbar({
        message: "Property transfer failed",
        status: "error",
      });
      logger.sendError(error as Error);
    }
  };

  return { handleSubmit, loading };
};

const TransferPropertyForm: React.FC<{
  property: PropertyData;
  loading: boolean;
  onClose: () => void;
  onSubmit: (values: { organizationId: string }) => Promise<void>;
}> = ({ property, loading, onClose, onSubmit }) => (
  <Formik
    initialValues={{ organizationId: "", confirmed: false }}
    validationSchema={buildFormSchema(property.organization?.id)}
    onSubmit={onSubmit}
  >
    {({ values }) => (
      <Form>
        <DialogContent>
          <p>
            Transfer this property from <strong>{property.organization?.name}</strong> to a
            new organization. This runs in the background and may take a few minutes for
            large waitlists. Users in the current organization with access to this
            property&apos;s interest list will lose that access.
          </p>
          <HavenForm>
            <SelectOrganization
              name="organizationId"
              label="New Organization *"
              showLabel
            />
            <Alert
              severity="warning"
              sx={{
                mt: 2,
                alignItems: "center",
                "& .MuiAlert-icon": {
                  alignSelf: "center",
                  py: 0,
                },
                "& .MuiAlert-message": {
                  py: 0,
                  width: "100%",
                },
              }}
            >
              <Checkbox
                name="confirmed"
                label="I understand that users in the current organization will lose access to this property's interest lists."
              />
            </Alert>
          </HavenForm>
        </DialogContent>
        <DialogActions>
          <Button size="large" color="secondary" variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <SubmitButton
            color="primary"
            disabled={loading || !values.confirmed}
          >
            Transfer
          </SubmitButton>
        </DialogActions>
      </Form>
    )}
  </Formik>
);

const TransferPropertyModal: React.FC<TransferPropertyModalProps> = ({
  property,
  open,
  onClose,
}) => {
  const { handleSubmit, loading } = useTransferProperty(property, onClose);

  if (!property) return null;

  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      title={`Transfer ${property.name}`}
    >
      <TransferPropertyForm
        property={property}
        loading={loading}
        onClose={onClose}
        onSubmit={handleSubmit}
      />
    </Modal>
  );
};

export default TransferPropertyModal;
